import { useEffect, useState } from 'react';
import { userSession } from '../utils/userSession.js';
import ImageUrlField from './ImageUrlField.js';
import { uploadImageAsset } from '../utils/imageUpload.js';
import DashboardModal from './DashboardModal.js';
import FormTabs, { FormTabPanel } from './FormTabs.js';

const TABS = [
  { id: 'business', label: 'Business' },
  { id: 'contact', label: 'Contact' }
];

export default function AddPartner({ isOpen, onClose, onPartnerCreated }) {
  const [tab, setTab] = useState('business');
  const [formData, setFormData] = useState({
    partnerName: '',
    partnerContact: '',
    partnerEmail: '',
    partnerPhone: '',
    partnerWebsiteLink: '',
    partnerPublicEmail: '',
    partnerLogo: '',
    partnerObjLink: '',
    partnerObjImage: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingFiles, setPendingFiles] = useState({
    PartnerLogo: null,
    PartnerObjImage: null
  });

  useEffect(() => {
    if (isOpen) {
      setTab('business');
      setPendingFiles({ PartnerLogo: null, PartnerObjImage: null });
      setError('');
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await fetch(
        'https://r7ej0e9i28.execute-api.ap-southeast-2.amazonaws.com/dev/partners',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ...formData,
            userId: userSession.getUserId()
          })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 409) {
          setError('A partner with this name already exists.');
        } else {
          throw new Error(data.message || data.error || 'Failed to register partner');
        }
        return;
      }

      const partnerId = data.userTypeDetails?.LinkID
        || data.userTypeDetails?.linkID
        || data.userTypeDetails?.linkId;
      let partnerLogo = formData.partnerLogo;
      let partnerObjImage = formData.partnerObjImage;
      const sessionToken = userSession.getUser()?.sessionToken;
      if (pendingFiles.PartnerLogo) {
        partnerLogo = await uploadImageAsset({
          type: 'PartnerLogo',
          entityId: partnerId,
          file: pendingFiles.PartnerLogo,
          sessionToken
        });
      }
      if (pendingFiles.PartnerObjImage) {
        partnerObjImage = await uploadImageAsset({
          type: 'PartnerObjImage',
          entityId: partnerId,
          file: pendingFiles.PartnerObjImage,
          sessionToken
        });
      }
      if (pendingFiles.PartnerLogo || pendingFiles.PartnerObjImage) {
        const update = await fetch(
          `https://r7ej0e9i28.execute-api.ap-southeast-2.amazonaws.com/dev/partners/${partnerId}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              ...formData,
              partnerLogo,
              partnerObjImage,
              userId: userSession.getUserId()
            })
          }
        );
        if (!update.ok) {
          throw new Error('Partner saved but the image URL could not be stored.');
        }
      }

      const currentUser = userSession.getUser();
      const updatedUser = {
        ...currentUser,
        userTypes: [...currentUser.userTypes, 3],
        userTypeDetails: [...(currentUser.userTypeDetails || []), {
          typeId: 3,
          approved: 1,
          linkId: partnerId
        }]
      };

      userSession.setUser(updatedUser);

      if (onPartnerCreated) {
        await onPartnerCreated();
      }
      onClose();
    } catch (err) {
      console.error('Error registering partner:', err);
      setError(`Failed to register partner: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <DashboardModal
      title="Register as Partner"
      onClose={onClose}
      onSubmit={handleSubmit}
      isLoading={isLoading}
      error={error}
      onRevealTab={setTab}
      tabs={<FormTabs tabs={TABS} active={tab} onChange={setTab} />}
    >
      <FormTabPanel id="business" active={tab}>
        <div>
          <label htmlFor="partnerName" className="block text-sm font-medium text-gray-700">
            Business Name *
          </label>
          <input
            type="text"
            id="partnerName"
            name="partnerName"
            required
            value={formData.partnerName}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-slate-500 focus:ring-slate-500"
          />
        </div>

        <div>
          <label htmlFor="partnerPublicEmail" className="block text-sm font-medium text-gray-700">
            Public Email
          </label>
          <input
            type="email"
            id="partnerPublicEmail"
            name="partnerPublicEmail"
            value={formData.partnerPublicEmail}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-slate-500 focus:ring-slate-500"
          />
        </div>

        <div>
          <label htmlFor="partnerWebsiteLink" className="block text-sm font-medium text-gray-700">
            Website Link
          </label>
          <input
            type="url"
            id="partnerWebsiteLink"
            name="partnerWebsiteLink"
            value={formData.partnerWebsiteLink}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-slate-500 focus:ring-slate-500"
          />
        </div>

        <ImageUrlField
          id="partnerLogo"
          name="partnerLogo"
          label="Logo URL"
          type="PartnerLogo"
          value={formData.partnerLogo}
          onChange={(partnerLogo) => setFormData((prev) => ({ ...prev, partnerLogo }))}
          pendingFile={pendingFiles.PartnerLogo}
          onPendingFile={(file) => setPendingFiles((prev) => ({ ...prev, PartnerLogo: file }))}
        />

        <div>
          <label htmlFor="partnerObjLink" className="block text-sm font-medium text-gray-700">
            Banner Web Link
          </label>
          <input
            type="url"
            id="partnerObjLink"
            name="partnerObjLink"
            value={formData.partnerObjLink}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-slate-500 focus:ring-slate-500"
          />
        </div>

        <ImageUrlField
          id="partnerObjImage"
          name="partnerObjImage"
          label="Banner Image URL (900x150px)"
          type="PartnerObjImage"
          value={formData.partnerObjImage}
          onChange={(partnerObjImage) => setFormData((prev) => ({ ...prev, partnerObjImage }))}
          pendingFile={pendingFiles.PartnerObjImage}
          onPendingFile={(file) => setPendingFiles((prev) => ({ ...prev, PartnerObjImage: file }))}
        />
      </FormTabPanel>

      <FormTabPanel id="contact" active={tab}>
        <div>
          <label htmlFor="partnerContact" className="block text-sm font-medium text-gray-700">
            Contact Name *
          </label>
          <input
            type="text"
            id="partnerContact"
            name="partnerContact"
            required
            value={formData.partnerContact}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-slate-500 focus:ring-slate-500"
          />
        </div>

        <div>
          <label htmlFor="partnerPhone" className="block text-sm font-medium text-gray-700">
            Contact Phone *
          </label>
          <input
            type="tel"
            id="partnerPhone"
            name="partnerPhone"
            required
            value={formData.partnerPhone}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-slate-500 focus:ring-slate-500"
          />
        </div>

        <div>
          <label htmlFor="partnerEmail" className="block text-sm font-medium text-gray-700">
            Contact Email *
          </label>
          <input
            type="email"
            id="partnerEmail"
            name="partnerEmail"
            required
            value={formData.partnerEmail}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-slate-500 focus:ring-slate-500"
          />
        </div>
      </FormTabPanel>
    </DashboardModal>
  );
}
